import React from "react";
import './CaseStudyReturns.css';
import { motion } from 'framer-motion';
import screenerStats from '../Assets/images/casestudy/returnssurveystats.png';
import figjamboard from '../Assets/images/casestudy/returnsfigjamboard.png';
import buytryreturn from '../Assets/images/casestudy/returncycle.png'; // Placeholder for the third image
import multisize from '../Assets/images/casestudy/returnsmultisize.png'; // Placeholder for the third image
import sizeguide from '../Assets/images/casestudy/returnssizeguide.png'; // Placeholder for the third image
import insights from '../Assets/images/casestudy/returnsfindings.png'; // Placeholder for the third image

export default function CaseStudyReturns() {
  return (
    <div className="caseStudyContainer">
{/* === HERO SECTION: TITLE & TASKS === */}
<section className="caseStudyIntroWrapper">
  <div className="caseStudyIntroSplit">
    <div className="intro-left">
      <h1 className="caseStudyTitle small">
        <span>Reducing Online Clothing Returns</span>
      </h1>
      <p className="caseStudyIntro">
        A deep dive into the behaviors, challenges, and UX impact of online shopping returns —
        and how we improved the user experience and facilitated better outcomes by addressing sizing uncertainty.
      </p>
    </div>
    <div className="intro-right">
    <h1 className="TasksTitle">Tasks</h1>
      <ul className="caseStudyList">
        <li>UX Research</li>
        <li>UX/UI Design</li>
        <li>Prototyping</li>
        <li>Usability Testing</li>
        <li>User Outreach</li>
      </ul>
    </div>
  </div>
</section>

{/* === SEPARATE KEY ACCOMPLISHMENTS SECTION === */}
<section className="caseStudyContainer">
  <motion.div
    className="caseStudyBlock"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, ease: 'easeOut' }}
  >
    <ul className="caseStudyList">
      <li>Led successful interviews providing actionable insights into user preferences</li>
      <li>Enabled data-backed design iterations via user testing</li>
      <li>Delivered validated solutions for users problems</li>
    </ul>
  </motion.div>
</section>

      <motion.section className="caseStudySection" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: 'easeOut' }}>
        <div className="caseStudyBlock">
          <h2 className="caseStudySubheading">🎯 Project Summary</h2>
          <p className="caseStudyText">
            This UX research project explores behaviors, pain points, and motivations behind clothing returns
            among online shoppers. Our goal: uncover insights to improve the sizing and return experience in e-commerce. And following that, design and test solutions to reduce returns and enhance user satisfaction.
            Stricly, nobody benefits from the massive amounts of returns clothing e-commerce. As we found out, it has a neutral - negative impact on the user experience, a hugely negative impact on the environment, and of course a major impact on the retailers earnings.
          </p>

        </div>
      </motion.section>

      <motion.section className="caseStudySection" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: 'easeOut' }}>
        <div className="caseStudyContent">
          <div className="caseStudyTextBlock">
            <h2 className="caseStudySubheading">👥 Target Audience & Participant Selection</h2>
            <p className="caseStudyText">
              We focused on frequent online shoppers who often return items. A screener survey was distributed to
              identify high-value users. Interviews were prioritized face-to-face, with remote sessions when needed.
            </p>
          </div>
          <img src={screenerStats} alt="Screener survey or interview process" className="caseStudyImage" />
        </div>
      </motion.section>

      <motion.section className="caseStudySection" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: 'easeOut' }}>
        <div className="caseStudyContent">
          <div className="caseStudyTextBlock">
            <h2 className="caseStudySubheading">🔍 Research Methodology</h2>
            <p className="caseStudyText">
              We carefully selected from our participants and with them, conducted five qualitative interviews to later make a thematic analysis with FigJam. Color-coded quotes
              and categorized insights helped us discover trends in behavior and attitudes.
            </p>
          </div>
          <img src={figjamboard} alt="FigJam whiteboard screenshot" className="caseStudyImage" />
        </div>
      </motion.section>

      <motion.section className="caseStudySection" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: 'easeOut' }}>
        <div className="caseStudyContent">
        <img src={insights} alt="Personas or quotes from high-value participants" className="caseStudyImage" />

          <div className="caseStudyTextBlock">
            <h2 className="caseStudySubheading">💡 Key Findings</h2>
            <ul className="caseStudyList">
              <li>Users overwhelmingly cited sizing errors in all their forms as the factor for making a return.</li>
              <li>Misleading illustrations, inconsistencies between sizes.</li>
              <li>Attitudes towards returning clothing are mixed based on climate impact awareness, and tolerance for the logistics</li>
            </ul>
          </div>
          
        </div>

      </motion.section>

      <motion.section className="caseStudySection" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: 'easeOut' }}>
        <div className="caseStudyContent">
          <div className="caseStudyTextBlock">
            <h2 className="caseStudySubheading">🚧 Pain Points</h2>
            <ul className="caseStudyList">
              <li>Generic or unclear size guides.</li>
              <li>Inconsistency across brands and products.</li>
              <li>Inability to judge the garment online.</li>
            </ul>
          </div>
        </div>
      </motion.section>

      <motion.section className="caseStudySection" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: 'easeOut' }}>
        <div className="caseStudyContent">
          <div className="caseStudyTextBlock">
            <h2 className="caseStudySubheading">🌿 Sustainability Awareness</h2>
            <p className="caseStudyText">
              Awareness of return-related environmental impact was mixed, and in itself it was not a strong motivator for change.
              However, many users expressed a desire to reduce their impact and were open to solutions that would help them do so.
              One user expressed the positive response they had to receiving sporting gear they knew had been tried on and returned.
            </p>
          </div>
          <img src={buytryreturn} alt="Return lifecycle and environmental impact" className="caseStudyImage" />
        </div>
      </motion.section>

      <motion.section className="caseStudySection" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: 'easeOut' }}>
        <div className="caseStudyContent">
          <div className="caseStudyTextBlock">
            <h2 className="caseStudySubheading">📌 Solution 1: Personal Size Profiles</h2>
            <p className="caseStudyText">
              We started with a system where the user would be able to save their sizes on their user profile. This would in turn
              provide vital data for us to tailor their experience on the site through notations of size selection with recommendations
              about their size choice referencing other customers’ experiences regarding the specific clothing item.
            </p>
          </div>
        </div>
      </motion.section>

      <motion.section className="caseStudySection" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: 'easeOut' }}>
        <div className="caseStudyContent">
          <div className="caseStudyTextBlock">
            <h2 className="caseStudySubheading">📌 Solution 2: Model-Based Contextual Sizing</h2>
            <p className="caseStudyText">
              We provided further contextual size information by showing the item on multiple different models, along with what size
              was used on each model. For different types of clothing items this is vital, and generally this is information that can
              only be gathered by trying the clothing item on.
            </p>
          </div>
          <img src={multisize} alt="Different models with size tags" className="caseStudyImage" />
        </div>
      </motion.section>

      <motion.section className="caseStudySection" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: 'easeOut' }}>
        <div className="caseStudyContent">
          <div className="caseStudyTextBlock">
            <h2 className="caseStudySubheading">📌 Solution 3: Global & Visual Size Guide</h2>
            <p className="caseStudyText">
              Finally, we created a full visual guide on sizes including: global translation of size standards, guides on measuring
              different parts of the body which, when implemented together with the size profile, would help customers make more
              confident and informed decisions when choosing clothing sizes.
              We had the privilege of working with a user who had reading difficulties, who responded positively to this solution.
            </p>
          </div>
          <img src={sizeguide} alt="Global sizing and measurement guide" className="caseStudyImage" />
        </div>
      </motion.section>

      <footer className="caseStudyFooter">
        <a href="/" className="caseStudyBackLink">← Back to Home</a>
      </footer>
    </div>
  );
}